/* ==========================================
   Storage
========================================== */

class Storage {
    static has(key) {
        try {
            return localStorage.getItem(key) === '1';
        } catch (error) {
            return false;
        }
    }

    static set(key) {
        try {
            localStorage.setItem(key, '1');
        } catch (error) {
            // Ignore storage failures
        }
    }
}

/* ==========================================
   Validation
========================================== */

class Validation {
    static email(value) {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return pattern.test(String(value || '').trim());
    }

    static phone(value) {
        if (!value) return true;
        const normalized = String(value).replace(/\D/g, '');
        return /^[0-9]{10,15}$/.test(normalized);
    }

    static consent(checked) {
        return Boolean(checked);
    }
}

/* ==========================================
   Analytics
========================================== */

class Analytics {
    static track(eventName, data = {}) {
        // Replace with GA4 / GTM / Meta Pixel hooks later if needed.
        console.log('[Newsletter]', eventName, data);
    }
}

/* ==========================================
   Newsletter Popup
========================================== */

class NewsletterPopup {
    constructor(container) {
        this.popup = container;

        this.sectionId = container.dataset.sectionId || '';
        this.delay = Number(container.dataset.delay) * 1000 || 0;
        this.showOnce = container.dataset.showOnce === 'true';
        this.successClose = Number(container.dataset.successClose) || 0;

        this.storageKey = `newsletter-popup-${this.sectionId}`;
        this.isSubmitting = false;
        this.openTimer = null;
        this.successCloseTimer = null;

        this.overlay = this.popup.querySelector('.newsletter-popup__overlay');
        this.closeButton = this.popup.querySelector('.newsletter-popup__close');
        this.videos = this.popup.querySelectorAll('video');

        this.form = this.popup.querySelector('.newsletter-popup__form');
        this.formWrapper = this.popup.querySelector('.newsletter-popup__form-wrapper');
        this.submitButton = this.popup.querySelector('.newsletter-popup__submit');
        this.loader = this.popup.querySelector('.newsletter-popup__loader');
        this.message = this.popup.querySelector('.newsletter-popup__message');

        this.emailField = this.popup.querySelector('input[type="email"]');
        this.phoneField = this.popup.querySelector('input[type="tel"]');
        this.consentField = this.popup.querySelector(
            'input[name="accepts_marketing"], input[name="newsletter_consent"]'
        );

        // The popup can contain two .newsletter-popup__success blocks:
        // 1) a server-rendered success marker inside the form
        // 2) the actual success screen outside the form
        // We use the last one as the visible success screen.
        const successBlocks = this.popup.querySelectorAll('.newsletter-popup__success');
        this.successScreen = successBlocks.length ? successBlocks[successBlocks.length - 1] : null;

        // This exists only when Shopify has posted the form successfully.
        this.serverSuccessMarker = this.form ? this.form.querySelector('.newsletter-popup__success') : null;

        this.init();
    }

    init() {
        // If Shopify already posted successfully, show the success screen immediately.
        if (this.hasServerSuccess()) {
            this.bindEvents();
            this.open(true);
            this.showSuccessScreen();
            return;
        }

        // If the last submission produced form errors, open immediately so the user sees them.
        if (this.hasServerErrors()) {
            this.bindEvents();
            this.open(true);
            return;
        }

        if (this.showOnce && this.hasVisited()) {
            return;
        }

        this.bindEvents();
        this.startDelay();
    }

    bindEvents() {
        this.closeButton?.addEventListener('click', () => this.close());
        this.overlay?.addEventListener('click', () => this.close());

        document.addEventListener('keydown', (event) => {
            if (!this.popup.classList.contains('active')) return;

            if (event.key === 'Escape') {
                this.close();
            }
        });

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.pauseVideos();
            } else if (this.popup.classList.contains('active')) {
                this.playVideos();
            }
        });

        if (this.form) {
            this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        this.emailField?.addEventListener('blur', () => this.validateEmail());
        this.emailField?.addEventListener('input', () => {
            if (this.emailField.classList.contains('newsletter-popup__input--error')) {
                this.validateEmail();
            }
        });

        this.phoneField?.addEventListener('blur', () => this.validatePhone());
        this.phoneField?.addEventListener('input', () => {
            if (this.phoneField.classList.contains('newsletter-popup__input--error')) {
                this.validatePhone();
            }
        });

        this.consentField?.addEventListener('change', () => this.validateConsent());
    }

    hasServerErrors() {
        return Boolean(this.popup.querySelector('.newsletter-popup__errors'));
    }

    hasServerSuccess() {
        return Boolean(this.serverSuccessMarker);
    }

    startDelay() {
        if (this.delay <= 0) {
            this.open();
            return;
        }

        this.openTimer = window.setTimeout(() => {
            this.open();
        }, this.delay);
    }

    open(immediate = false) {
        if (this.openTimer) {
            clearTimeout(this.openTimer);
            this.openTimer = null;
        }

        this.popup.classList.add('active');
        document.documentElement.classList.add('newsletter-popup-open');

        if (immediate) {
            this.popup.classList.add('newsletter-popup--immediate');
        }

        this.playVideos();

        Analytics.track('newsletter_popup_open', {
            sectionId: this.sectionId,
        });
    }

    close() {
        if (this.openTimer) {
            clearTimeout(this.openTimer);
            this.openTimer = null;
        }

        if (this.successCloseTimer) {
            clearTimeout(this.successCloseTimer);
            this.successCloseTimer = null;
        }

        this.popup.classList.remove('active');
        this.popup.classList.remove('newsletter-popup--immediate');
        document.documentElement.classList.remove('newsletter-popup-open');

        this.pauseVideos();

        if (this.showOnce) {
            this.saveVisit();
        }

        Analytics.track('newsletter_popup_close', {
            sectionId: this.sectionId,
        });
    }

    validateEmail() {
        if (!this.emailField) return true;

        const value = this.emailField.value.trim();

        if (!value) {
            this.showFieldError(this.emailField, 'Email can’t be blank.');
            return false;
        }

        if (!Validation.email(value)) {
            this.showFieldError(this.emailField, 'Please enter a valid email address.');
            return false;
        }

        this.clearFieldError(this.emailField);
        return true;
    }

    validatePhone() {
        if (!this.phoneField) return true;

        const value = this.phoneField.value.trim();

        if (!value && !this.phoneField.required) {
            this.clearFieldError(this.phoneField);
            return true;
        }

        if (!Validation.phone(value)) {
            this.showFieldError(this.phoneField, 'Please enter a valid mobile number.');
            return false;
        }

        this.clearFieldError(this.phoneField);
        return true;
    }

    validateConsent() {
        if (!this.consentField) return true;

        if (!Validation.consent(this.consentField.checked)) {
            this.showFieldError(this.consentField, 'Please accept the consent checkbox.');
            return false;
        }

        this.clearFieldError(this.consentField);
        return true;
    }

    validateForm() {
        const emailValid = this.validateEmail();
        const phoneValid = this.validatePhone();
        const consentValid = this.validateConsent();

        return emailValid && phoneValid && consentValid;
    }

    showFieldError(field, message) {
        if (!field) return;

        field.classList.add('newsletter-popup__input--error');
        field.classList.remove('newsletter-popup__input--success');
        field.setAttribute('aria-invalid', 'true');

        const wrapper = field.closest('.newsletter-popup__form-group');
        const error = wrapper?.querySelector('.newsletter-popup__error');

        if (error) {
            error.textContent = message;
        }
    }

    clearFieldError(field) {
        if (!field) return;

        field.classList.remove('newsletter-popup__input--error');
        field.classList.add('newsletter-popup__input--success');
        field.setAttribute('aria-invalid', 'false');

        const wrapper = field.closest('.newsletter-popup__form-group');
        const error = wrapper?.querySelector('.newsletter-popup__error');

        if (error) {
            error.textContent = '';
        }
    }

    clearAllFieldErrors() {
        [this.emailField, this.phoneField, this.consentField].forEach((field) => {
            if (!field) return;
            this.clearFieldError(field);
        });
    }

    onSubmit(event) {
        event.preventDefault();

        if (this.isSubmitting) {
            return;
        }

        this.clearMessage();

        if (!this.validateForm()) {
            this.showError('Please fix the highlighted fields.');
            return;
        }

        this.isSubmitting = true;
        this.setLoading(true);

        Analytics.track('newsletter_submit_attempt', {
            sectionId: this.sectionId,
            email: this.emailField?.value || '',
        });

        // Native Shopify submission is the most stable path for the customer newsletter form.
        // The page will reload and Shopify can handle the success/error response server-side.
        window.setTimeout(() => {
            if (this.form) {
                this.form.submit();
            } else {
                this.setLoading(false);
                this.isSubmitting = false;
                this.showError('Form not found.');
            }
        }, 50);
    }

    setLoading(state) {
        if (!this.submitButton) return;

        this.submitButton.disabled = state;

        if (this.loader) {
            this.loader.hidden = !state;
        }

        const text = this.submitButton.querySelector('.newsletter-popup__submit-text');
        if (text) {
            text.style.opacity = state ? '.35' : '1';
        }
    }

    showSuccessScreen() {
        if (this.formWrapper) {
            this.formWrapper.hidden = true;
        }

        if (this.message) {
            this.clearMessage();
        }

        if (this.successScreen) {
            this.successScreen.hidden = false;
        }

        this.popup.classList.add('newsletter-popup--success');

        Analytics.track('newsletter_submit_success', {
            sectionId: this.sectionId,
            email: this.emailField?.value || '',
            phone: this.phoneField?.value || '',
        });

        if (this.showOnce) {
            this.saveVisit();
        }

        if (this.successClose > 0) {
            if (this.successCloseTimer) {
                clearTimeout(this.successCloseTimer);
            }

            this.successCloseTimer = window.setTimeout(() => {
                this.close();
            }, this.successClose * 1000);
        }
    }

    showSuccess(message) {
        if (!this.message) return;

        this.message.className = 'newsletter-popup__message newsletter-popup__message--success';
        this.message.textContent = message;
    }

    showError(message) {
        if (!this.message) return;

        this.message.className = 'newsletter-popup__message newsletter-popup__message--error';
        this.message.textContent = message;
    }

    clearMessage() {
        if (!this.message) return;

        this.message.className = 'newsletter-popup__message';
        this.message.textContent = '';
    }

    playVideos() {
        this.videos.forEach((video) => {
            try {
                video.currentTime = 0;

                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise.catch(() => {
                        // Browser blocked autoplay
                    });
                }
            } catch (error) {
                // Ignore playback issues
            }
        });
    }

    pauseVideos() {
        this.videos.forEach((video) => {
            try {
                video.pause();
            } catch (error) {
                // Ignore pause issues
            }
        });
    }

    hasVisited() {
        return Storage.has(this.storageKey);
    }

    saveVisit() {
        Storage.set(this.storageKey);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.newsletter-popup').forEach((popup) => {
        new NewsletterPopup(popup);
    });
});